<template>
    <div class="w-100">
        <pc-loader
            v-bind:loading="saving"
            v-bind:message="savingText">
            <div
                ref="conversation"
                class="inquiry-conversation my-2"
                tabindex="0">
                <div
                    v-for="(n, i) in notes"
                    v-bind:key="'pnw-' + i"
                    class="d-inline-block w-100">
                    <div
                        v-bind:class="{
                            'project-note-user': !n.AdminNote && !n.StatusChangeNote,
                            'project-note-admin': n.AdminNote
                        }"
                        class="d-block p-2 mt-2 overflow-hidden float-start rounded">
                        {{ n.Header }}
                        <div class="project-note-content fst-italic ms-3">
                            <p>{{ n.Content }}</p>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="newReplyMode">
                <hr />
                <pc-textarea
                    id="NewNoteInput"
                    v-model="newNoteContent"
                    v-bind:label="addReplyColMsg"
                    v-bind:maxlength="noteCharacterLimit"
                    v-bind:validator="newReplyValidator" />
                <div class="mb-3 text-body-secondary fst-italic">
                    {{ remainingChar }}
                </div>
                <div class="text-end mt-2">
                    <pc-button-row>
                        <template v-slot:right>
                            <pc-button
                                id="NewNoteCancelBtn"
                                v-on:click="newReplyMode = false">
                                {{ cancelText }}
                            </pc-button>
                            <pc-button
                                id="NewNoteSaveBtn"
                                primary
                                v-bind:disabled="saveDisabled"
                                v-on:click="saveNewNote">
                                {{ saveText }}
                            </pc-button>
                        </template>
                    </pc-button-row>
                </div>
            </div>
            <div v-else-if="changeStatusMode">
                <hr />
                {{ currentInqStatus }} <br />
                <br />
                <pc-loader
                    v-bind:loading="Boolean(loadingMsg)"
                    v-bind:message="loadingMsg">
                    <div class="row">
                        <pc-select
                            id="NewStatusSelect"
                            v-model="selectedStatus"
                            v-bind:class="responsiveClasses.inquiryStatusSelect"
                            v-bind:label="selectNewStatusMsg"
                            v-bind:optgroups="inqStatusGroups"
                            value-prop="StatusName"
                            text-prop="StatusName">
                            <option value="">
                                {{ selectText }}
                            </option>
                        </pc-select>
                    </div>
                    <div class="text-end mt-3">
                        <pc-button-row>
                            <template v-slot:right>
                                <pc-button
                                    id="SetStatusCancelBtn"
                                    v-on:click="changeStatusMode = false">
                                    {{ cancelText }}
                                </pc-button>
                                <pc-button
                                    id="SetStatusSaveBtn"
                                    primary
                                    v-bind:disabled="saveDisabled"
                                    v-on:click="setThreadStatus">
                                    {{ saveText }}
                                </pc-button>
                            </template>
                        </pc-button-row>
                    </div>
                </pc-loader>
            </div>
            <div v-else-if="threadSmsNotificationsMode">
                <hr />
                <div class="ml-1">
                    <pc-checkbox
                        id="enableSmsNotificationsCheckbox"
                        v-model="enableSmsNotifications"
                        v-bind:label="modifySmsNotificationsLabel"
                        v-bind:disabled="!hasVerifiedPhoneNumber" />
                    <div
                        v-if="!hasVerifiedPhoneNumber"
                        class="alert alert-warning">
                        {{ noVerifiedPhoneNumberMsg }}
                        <strong>
                            <a v-bind:href="`${manageAccountMenuItem.Url}?ProgramId=${$app.ProgramId}`">
                                {{ manageAccountMenuItem.Text }}
                            </a>
                        </strong>
                    </div>
                </div>
                <div class="text-end mt-3">
                    <pc-button-row>
                        <template v-slot:right>
                            <pc-button
                                id="ThreadSmsNotificationsCancelBtn"
                                v-on:click="threadSmsNotificationsMode = false">
                                {{ cancelText }}
                            </pc-button>
                            <pc-button
                                id="ThreadSmsNotificationsSaveBtn"
                                primary
                                v-on:click="setThreadSmsNotifications">
                                {{ saveText }}
                            </pc-button>
                        </template>
                    </pc-button-row>
                </div>
            </div>
            <div v-else>
                <div style="text-align: right">
                    <hr />
                    <pc-button-row>
                        <template v-slot:right>
                            <pc-button
                                v-if="Boolean(landingPageBase)"
                                id="GoToLandingPageBtn"
                                v-on:click="goToLandingPage">
                                {{ gotoViewEditMsg }}
                            </pc-button>
                            <pc-button
                                v-if="Boolean(adminPageBase)"
                                id="GoToAdminPageBtn"
                                v-on:click="goToAdminPage">
                                {{ gotoAdminMsg }}
                            </pc-button>
                            <pc-button
                                v-if="isAdmin"
                                id="SetStatusBtn"
                                v-bind:primary="threadClosed"
                                v-on:click="showSetInquiryStatusDialog">
                                {{ changeInqStatusMsg }}
                            </pc-button>
                            <pc-button
                                v-if="!threadClosed"
                                id="AddReplyBtn"
                                primary
                                v-on:click="newReplyMode = true">
                                {{ addReplyMsg }}
                            </pc-button>
                            <pc-button
                                v-if="!threadClosed && smsFeaturesEnabled"
                                id="threadSmsNotificationsBtn"
                                v-on:click="showSmsNotificationsDialog">
                                {{ threadSmsNotificationsMsg }}
                            </pc-button>
                        </template>
                    </pc-button-row>
                </div>
            </div>
        </pc-loader>
    </div>
</template>
<script>
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { WIDE_BP } from "Projects/SharedCapsules/_sharedCapsuleBase";
    import { getResponsiveClassMap } from "Responsive/manager";
    import { KEYS, NAMESPACE } from "./i18n/inquiry-thread.i18n.js";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { mapI18n } from "Util/i18n";
    import { MENU_TYPE_NAME } from "Components/page/Navigation/menuTypeName";

    // Technical limit is higher, but 4000 seemed a good usablility limit.
    const NEW_NOTE_CHARACTER_LIMIT = 4000;
    const responsiveConfig = {
        inquiryStatusSelect: {
            col: { xs: 12, [WIDE_BP.name]: 6, lg: 4, xl: 3 }
        }
    };

    export default {
        name: "InquiryThread",
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            programId: { type: String, required: true },
            projectId: { type: String, required: true },
            threadId: { type: String, required: true },
            notes: { type: Array, required: true },
            threadClosed: { type: Boolean, required: true },
            threadStatus: { type: String, required: true },
            activeStatuses: { type: Array, required: true },
            landingPageBase: { type: String, required: false, default: null },
            adminPageBase: { type: String, required: false, default: null },
            smsFeaturesEnabled: { type: Boolean, required: true },
            smsNotificationStatus: { type: Boolean, required: true }
        },
        emits: ["inq-save-note", "inq-set-status"],
        data: function () {
            return {
                newNoteContent: null,
                newReplyMode: false,
                changeStatusMode: false,
                loadingMsg: null,
                selectedStatus: "",
                noteCharacterLimit: NEW_NOTE_CHARACTER_LIMIT,
                saving: false,
                threadSmsNotificationsMode: false,
                localSmsNotificationStatus: this.smsNotificationStatus,
                hasVerifiedPhoneNumber: false
            };
        },
        computed: {
            isAdmin() {
                return this.$app.LoggedInRole.IsProgramAdmin;
            },
            newReplyValidator() {
                return getValidator(this.$app, [
                    {
                        type: VALIDATOR_TYPES.required,
                        overrideMsg: this.replyCannotBeBlank
                    },
                    {
                        type: VALIDATOR_TYPES.maxLength,
                        maxLength: NEW_NOTE_CHARACTER_LIMIT
                    }
                ]);
            },
            saveDisabled() {
                return (this.newReplyMode && !this.newNoteContent) || (this.changeStatusMode && !this.selectedStatus);
            },
            inqStatusGroups() {
                const groups = [
                    {
                        name: this.openStatusesMsg,
                        options: this.activeStatuses.filter((s) => !s.IsClosed && s.StatusName !== this.threadStatus)
                    },
                    {
                        name: this.closedStatusesMsg,
                        options: this.activeStatuses.filter((s) => s.IsClosed && s.StatusName !== this.threadStatus)
                    }
                ];
                return groups.filter((g) => g.options.length > 0);
            },
            selectedStatusObj() {
                return this.activeStatuses.find((s) => s.StatusName === this.selectedStatus);
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            //Calculates number of characters available in the reply message
            replyRemainingChars() {
                if (!this.newNoteContent) {
                    return NEW_NOTE_CHARACTER_LIMIT;
                }
                const remaining = NEW_NOTE_CHARACTER_LIMIT - this.newNoteContent.length;
                return remaining > 0 ? remaining : 0;
            },
            ...mapI18n(
                {
                    ...KEYS,
                    remainingChar() {
                        return {
                            key: KEYS.remainingCharMsg,
                            values: { charactersRemaining: this.replyRemainingChars }
                        };
                    },
                    currentInqStatus() {
                        return {
                            key: KEYS.currentInqStatusMsg,
                            values: { inqStatus: this.threadStatus }
                        };
                    }
                },
                NAMESPACE
            ),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            enableSmsNotifications: {
                get() {
                    return this.localSmsNotificationStatus;
                },
                set(value) {
                    this.localSmsNotificationStatus = value;
                }
            },
            manageAccountMenuItem() {
                return this.$app.Navigation.find((navMenu) => navMenu.MenuTypeName === MENU_TYPE_NAME.ManageAccount);
            }
        },
        watch: {
            notes: {
                handler() {
                    const conversationContainer = this.$refs.conversation;
                    this.$nextTick(() => (conversationContainer.scrollTop = conversationContainer.scrollHeight));
                },
                deep: true
            }
        },
        async mounted() {
            this.hasVerifiedPhoneNumber = await this.getUserHasVerifiedPhoneNumber();
        },
        methods: {
            goToLandingPage() {
                window.open(
                    `${this.landingPageBase}?ProgramId=${this.$app.ProgramId}&ProjectId=${this.projectId}`,
                    "_blank"
                );
            },
            goToAdminPage() {
                window.open(`${this.adminPageBase}&ProjectId=${this.projectId}`, "_blank");
            },
            async saveNewNote() {
                if (!this.threadId) {
                    return;
                }

                if (this.saving) {
                    return;
                }

                this.saving = true;
                try {
                    this.newReplyMode = false;
                    const result = await this.$serviceControllers.ProjectInquiriesController.AddNewReply({
                        programId: this.programId,
                        projectId: this.projectId,
                        threadId: this.threadId,
                        noteContent: this.newNoteContent
                    });
                    if (result.status === "success") {
                        this.$emit("inq-save-note");
                        this.newNoteContent = null;
                    }
                } finally {
                    this.saving = false;
                }
            },
            async setThreadStatus() {
                this.loadingMsg = this.savingText;
                const result = await this.$serviceControllers.ProjectInquiriesController.SetThreadStatus({
                    programId: this.programId,
                    projectId: this.projectId,
                    threadId: this.threadId,
                    statusName: this.selectedStatus
                });
                if (result.status === "success") {
                    this.$emit("inq-set-status", this.selectedStatusObj);
                    this.changeStatusMode = false;
                    this.selectedStatus = null;
                    this.loadingMsg = null;
                }
            },
            async setThreadSmsNotifications() {
                const result =
                    await this.$serviceControllers.ProjectInquiriesController.UpdateProjectInquirySmsNotificationStatus(
                        {
                            programId: this.programId,
                            threadId: this.threadId,
                            enableSmsNotifications: this.enableSmsNotifications
                        }
                    );
                if (result.status === "success") {
                    this.threadSmsNotificationsMode = false;
                }
            },
            showSetInquiryStatusDialog() {
                this.changeStatusMode = true;
            },
            showSmsNotificationsDialog() {
                this.threadSmsNotificationsMode = true;
            },
            async getUserHasVerifiedPhoneNumber() {
                const result =
                    await this.$serviceControllers.ProjectInquiriesController.GetUserHasVerifiedPhoneNumber();
                return result.Data.HasVerifiedPhoneNumber;
            }
        }
    };
</script>

<style lang="scss" scoped>
    div.inquiry-conversation {
        max-height: 30rem;
        overflow-y: auto;
    }

    div.project-note-user {
        border: 1px solid var(--bs-gray-600);
        background-color: var(--bs-primary-100);
    }

    div.project-note-admin {
        border: 1px solid var(--bs-gray-600);
        background-color: var(--bs-primary-200);
    }

    div.project-note-content {
        white-space: pre-wrap;
    }
</style>
